import {Lang} from "./Locale";

export type ZpDate = number;

export type ZpCult = "en-US" | "en-GB" | Exclude<Lang, "en">;
export type ZpWeekdays = 0 | 1 | 2 | 3 | 4 | 5 | 6 // 0 = Sonntag, 1 = Montag

export class Zp {
    static cult: ZpCult = "en-US";

    static init(lang: Lang, country: string) {
        Zp.cult = lang !== "en" ? lang : (country == "GB" ? "en-GB" : "en-US");
    }

    static fromParts(day: number, month: number, year: number): ZpDate
    static fromParts(parts: number | {
        day: number,
        month: number,
        year: number
    }, month?: number, year?: number): ZpDate
    static fromParts(day: number | {
        day: number,
        month: number,
        year: number
    }, month?: number, year?: number): ZpDate {
        if (typeof day != "number") {
            year = day.year;
            month = day.month;
            day = day.day;
        }
        let dcode = (((year - 2000) * 36525 / 100) | 0) + day;
        if ((year & 3) === 0) {
            dcode--;
            if (month > 2) dcode++;
        }
        return dcode + Zp._dayCodes[month];
    }

    static toParts(zpDate: ZpDate): { day: number, month: number, year: number } {
        let y = ((zpDate * 100) / 36525) | 0;
        zpDate -= ((y * 36525) / 100) | 0;
        y += 2000;
        let d: ZpDate;
        let m: number;
        if (zpDate < 152) {
            if (zpDate < 60) {
                if ((y & 3) === 0) {
                    if (zpDate < 31) {
                        d = zpDate + 1;
                        m = 1;
                    } else {
                        d = zpDate - 30;
                        m = 2;
                    }
                } else {
                    if (zpDate < 32) {
                        d = zpDate;
                        m = 1;
                    } else {
                        d = zpDate - 31;
                        m = 2;
                    }
                }
            } else {
                if (zpDate < 91) {
                    d = zpDate - 59;
                    m = 3;
                } else {
                    if (zpDate < 121) {
                        d = zpDate - 90;
                        m = 4;
                    } else {
                        d = zpDate - 120;
                        m = 5;
                    }
                }
            }
        } else {
            if (zpDate < 244) {
                if (zpDate < 213) {
                    if (zpDate < 182) {
                        d = zpDate - 151;
                        m = 6;
                    } else {
                        d = zpDate - 181;
                        m = 7;
                    }
                } else {
                    d = zpDate - 212;
                    m = 8;
                }
            } else {
                if (zpDate < 305) {
                    if (zpDate < 274) {
                        d = zpDate - 243;
                        m = 9;
                    } else {
                        d = zpDate - 273;
                        m = 10;
                    }
                } else {
                    if (zpDate < 335) {
                        d = zpDate - 304;
                        m = 11;
                    } else {
                        d = zpDate - 334;
                        m = 12;
                    }
                }
            }
        }
        return {year: y, month: m, day: d};
    }

    static fromVacandoTs(ts: number): ZpDate {
        return ts / 864 - 730119;
    }

    static toVacandoTs(zpDate: ZpDate): number {
        return (zpDate + 730119) * 864;
    }

    static format(zpDate: ZpDate): string {
        const two = (n: number) => (n / 10 | 0) + "" + n % 10;
        const fmt = Zp._dateFormat[Zp.cult];
        if (fmt.length != 4) return "";
        const p = Zp.toParts(zpDate);
        let r = "";
        for (let i = 1; i < fmt.length; i++) {
            if (i > 1) r += fmt[0];
            if (fmt[i] == 'd') r += two(p.day); else if (fmt[i] == 'm') r += two(p.month); else if (fmt[i] == 'y') r += p.year;
        }
        return r;
    }

    static iso(zpDate: ZpDate): string {
        const two = (n: number) => (n / 10 | 0) + "" + n % 10;
        const p = Zp.toParts(zpDate);
        return p.year + "-" + two(p.month) + "-" + two(p.day);
    }

    static fromIso(iso: string): ZpDate {
        const [yearStr, monthStr, dayStr] = iso.split("-");
        const year = parseInt(yearStr, 10);
        const month = parseInt(monthStr, 10);
        const day = parseInt(dayStr, 10);
        return Zp.fromParts(day, month, year);
    }

    static weekday(zpDate: ZpDate): ZpWeekdays {
        return ((zpDate + 6) % 7 | 0) as ZpWeekdays;
    }

    static today(): ZpDate {
        const d = new Date();
        return Zp.fromParts(d.getUTCDate(), d.getUTCMonth() + 1, d.getUTCFullYear());
    }

    private static readonly _dayCodes = [0, 0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];

    static readonly monthName = {
        "en-US": ["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
        "en-GB": ["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
        "es": ["", "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"],
        "cs": ["", "Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"],
        "fr": ["", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"],
        "de": ["", "Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"],
        "da": ["", "Januar", "Februar", "Marts", "April", "Maj", "Juni", "Juli", "August", "September", "Oktober", "November", "December"],
        "it": ["", "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"],
        "nl": ["", "Januari", "Februari", "Maart", "April", "Mei", "Juni", "Juli", "Augustus", "September", "Oktober", "November", "December"],
        "pl": ["", "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec", "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"],
        "pt": ["", "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"],
        "zh": ["", "一月", "二月", "三月", "四月", "五月", "六月", "七月", "八月", "九月", "十月", "十一月", "十二月"],
        "sv": ["", "Januari", "Februari", "Mars", "April", "Maj", "Juni", "Juli", "Augusti", "September", "Oktober", "November", "December"],
        "nb": ["", "Januar", "Februar", "Mars", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Desember"],
        "fi": ["", "Tammikuu", "Helmikuu", "Maaliskuu", "Huhtikuu", "Toukokuu", "Kesäkuu", "Heinäkuu", "Elokuu", "Syyskuu", "Lokakuu", "Marraskuu", "Joulukuu"],
        "ru": ["", "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"],
    }

    static readonly weekdayStart = {
        "en-US": 0,
        "en-GB": 1,
        "es": 1,
        "cs": 1,
        "fr": 1,
        "de": 1,
        "da": 1,
        "it": 1,
        "nl": 1,
        "pl": 1,
        "pt": 1,
        "zh": 1,
        "sv": 1,
        "nb": 1,
        "fi": 1,
        "ru": 1,
    }

    static readonly weekdayShortName = {
        "en-US": ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
        "en-GB": ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
        "es": ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"],
        "cs": ["Ne", "Po", "Út", "St", "Čt", "Pá", "So"],
        "fr": ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"],
        "de": ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"],
        "da": ["Søn", "Man", "Tir", "Ons", "Tor", "Fre", "Lør"],
        "it": ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"],
        "nl": ["Zo", "Ma", "Di", "Wo", "Do", "Vr", "Za"],
        "pl": ["Nie", "Pon", "Wto", "Śro", "Czw", "Pią", "Sob"],
        "pt": ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"],
        "zh": ["周日", "周一", "周二", "周三", "周四", "周五", "周六"],
        "sv": ["Sön", "Mån", "Tis", "Ons", "Tor", "Fre", "Lör"],
        "nb": ["Søn", "Man", "Tir", "Ons", "Tor", "Fre", "Lør"],
        "fi": ["Su", "Ma", "Ti", "Ke", "To", "Pe", "La"],
        "ru": ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"],
    };

    private static readonly _dateFormat = {
        "en-US": "/mdy",
        "en-GB": "/dmy",
        "es": "/dmy",
        "cs": ".dmy",
        "fr": "/dmy",
        "de": ".dmy",
        "da": "-dmy",
        "it": "/dmy",
        "nl": "-dmy",
        "pl": ".dmy",
        "pt": "/dmy",
        "zh": "-ymd",
        "sv": "-ymd",
        "nb": ".dmy",
        "fi": ".dmy",
        "ru": ".dmy",
    };
}
